import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';

// Logs when a search never leaves the loading state and the user is stranded on the placeholder
// templates. Four lines, so "slow" can be told from "stuck":
//   REF1000 search loading stuck             — still on placeholders after LOADING_STUCK_TIMEOUT_MS
//   REF1000 search loading recovered         — one reported stuck did finish later; total wait
//   REF1000 search loading not recovered     — it never did: a new search replaced it while stuck
//   REF1000 search loading stuck after ready — still on placeholders after the data arrived
// count(stuck) - count(recovered) is the number that never recovered. "after ready" is a separate,
// higher-confidence signal, not a term in that subtraction.
//
// stage names the arming path and what it reached: newSearch | searchChanged | serverWait |
// destinationsReload, then :polling (the response was WAIT, so waitingCall took over) or
// :postResponseError (the catch in handleSearchChangedBase, which clears neither flag). No arming path
// in it means nobody named that stretch — never that it inherited the previous search's.
//
// Driven from the component that renders the placeholders (SearchPageContainer, isLoading || isWaiting)
// rather than from the functions that set those flags, because the whole-object writes of searchPageData
// bypass those setters. Neither call site cleans up on unmount, deliberately: a destinations search that
// resolves to results swaps IndexClient for SearchPageContainer mid-wait without the placeholders
// leaving the screen, so the mounting component re-arms into the live timer and the stretch stays one
// measurement. The cost is that a detail-page detour keeps the clock and the `reported` latch running
// across it — the pageType guard below only suppresses lines emitted while the user is away.
//
// NOTE: only reports while the tab is open, so pages abandoned while stuck are under-counted.

// Wall clock, and 90s does NOT clear the longest legitimate wait: waitingCall polls up to count === 60
// one-second ticks, each given 20s and three retries by clientAPICall. A healthy full-budget search can
// therefore be reported stuck, and it is the recovered pairing rather than this threshold that keeps the
// count usable. Raise it if the stuck lines turn out to be dominated by ones that later recover.
export const LOADING_STUCK_TIMEOUT_MS = 90_000;

// The sharper signal: armed only once nothing is waiting on the search any more, so a slow backend
// cannot trip it. Clear of the deliberate 500ms delay before the destinations reload drops its flag.
export const READY_STUCK_TIMEOUT_MS = 10_000;

// An absent pageType still means the search page (getPageTypeFromString defaults to SEARCH), so only an
// explicit detail pageType suppresses a report.
const NON_SEARCH_PAGE_TYPES: string[] = [PAGE_TYPE.HOTEL_DETAIL, PAGE_TYPE.TRANSPORT_DETAIL, PAGE_TYPE.REVIEW];

let timeout: ReturnType<typeof setTimeout> | undefined;
let readyTimeout: ReturnType<typeof setTimeout> | undefined;
let startedAt = 0;
let reported = false;
let stage = '';

const log = (logMessage: string) => {
  // clientAppLog is sendBeacon-only, which the DevTools Fetch/XHR filter hides. Echo it in local dev.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(logMessage);
  }

  clientAppLog({
    level: 'WARN',
    codeSourceName: 'searchLoadingWatchdog',
    logMessage,
    url: window?.location?.href,
  });
};

const hasNavigatedAwayFromSearch = (): boolean =>
  NON_SEARCH_PAGE_TYPES.includes(getSearchParams()[URLParameters.PAGE_TYPE] as string);

/** Reports unless the user has navigated away; returns whether it did, so the caller can pair it. */
const logStuck = (variant: string, waitedMs: number): boolean => {
  if (hasNavigatedAwayFromSearch()) return false;

  log(
    `REF1000 search loading stuck${variant}. stage:${stage}, waitedMs:${waitedMs}, searchId:${getSearchParams()[URLParameters.SEARCH_ID]}`
  );
  return true;
};

/** Closes off a stretch already reported stuck, measured from when its clock started. */
const logStuckOutcome = (outcome: string) => {
  if (hasNavigatedAwayFromSearch()) return;

  log(`REF1000 search loading ${outcome}. stage:${stage}, waitedMs:${Date.now() - startedAt}`);
};

const armReady = () => {
  readyTimeout = setTimeout(() => {
    readyTimeout = undefined;
    logStuck(' after ready', Date.now() - startedAt);
  }, READY_STUCK_TIMEOUT_MS);
};

const arm = () => {
  startedAt = Date.now();
  timeout = setTimeout(() => {
    // A fired timer must not leave the watchdog looking armed.
    timeout = undefined;
    reported = logStuck('', Date.now() - startedAt);
    // Suppressed, so nothing will close this stretch off later: let go of its breadcrumb too.
    if (!reported) stage = '';
  }, LOADING_STUCK_TIMEOUT_MS);
};

/** Names the stretch. Call where a search starts — also where one already reported stuck is closed off. */
export const setSearchLoadingStage = (newStage: string) => {
  if (reported) {
    // Book it now, or this search's eventual success emits `recovered` against the old stretch.
    logStuckOutcome('not recovered');
    reported = false;
    // Nothing disarmed since the report, so the flags will not flip and re-arm. Start the clock here.
    arm();
  }

  stage = newStage;
  // The previous search's data did arrive; the placeholders are this search's now.
  clearTimeout(readyTimeout);
  readyTimeout = undefined;
};

/**
 * Names the stretch only if nothing has, for a path that cannot tell whether it started one — a server
 * snapshot in WAIT may have raised the placeholders, or may be landing mid-search. Returns whether it
 * claimed the stretch.
 */
export const setSearchLoadingStageIfUnnamed = (newStage: string): boolean => {
  if (stage) return false;

  stage = newStage;
  return true;
};

export const appendSearchLoadingStage = (extraStage: string) => {
  stage = stage ? `${stage}:${extraStage}` : extraStage;
};

/**
 * Call once nothing is waiting on the search any more: the entry of handleWaitingFinished, and the
 * destinations reload once its response has arrived. Arms only while the placeholders are on screen.
 */
export const watchSearchDataReady = () => {
  if (!timeout && !reported) return;
  if (readyTimeout) return;

  armReady();
};

/**
 * Same signal for a stretch that has not armed yet: a WAIT snapshot adopted with nothing polling behind
 * it can never resolve, so it need not wait out the wall clock. Safe to arm a commit early — the disarm
 * of the commit that raises the placeholders drops it if nothing is stranded, and the arm in that same
 * commit sets startedAt before this can fire.
 */
export const watchSearchStranded = () => {
  if (readyTimeout) return;

  armReady();
};

/**
 * Call on every change of the rendered search loading state: true arms, false disarms. Arming is
 * idempotent, so the clock measures one continuous stretch of placeholders — including after the stuck
 * report, so it cannot be counted stuck twice and the disarm still reports the whole wait.
 */
export const watchSearchLoading = (isLoading: boolean) => {
  if (isLoading) {
    // `reported` too: the fired timer released its handle, and a re-arm would report the same stretch
    // twice and reset the clock under it.
    if (timeout || reported) return;

    arm();
    return;
  }

  clearTimeout(timeout);
  timeout = undefined;
  clearTimeout(readyTimeout);
  readyTimeout = undefined;

  if (reported) {
    logStuckOutcome('recovered');
    reported = false;
  }

  // The stretch is over; its breadcrumb must not leak into the next one.
  stage = '';
};
